import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  CalendarDays,
  LayoutDashboard,
  LogOut,
  ScanLine,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RealtimeProvider, useLiveData, useRealtime } from "@/lib/realtime";
import { fmtDate, idem } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Empty, Logo } from "@/components/Common";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RoutePanel, RouteTab } from "./RoutePanel";
import { ManualCheckinForm, ScanDialog } from "./Scan";
import { LeadVerificationDialog } from "./LeadVerificationDialog";

const ACT_LABEL = { call: "dipanggil", arrive: "hadir", start: "mulai climbing", complete: "selesai", skip: "di-skip" };

function Board({ eventId }) {
  const [b, setB] = useState(null);
  const [offset, setOffset] = useState(0);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(null);
  const [scan, setScan] = useState(false);
  const [manual, setManual] = useState(false);
  const [leadVerification, setLeadVerification] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/keeper/events/${eventId}/board`);
      setOffset(Date.parse(data.server_time) - Date.now());
      setB(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useLiveData(load, [eventId]);
  const act = async (tid, action) => {
    setBusy(`${tid}:${action}`);
    try {
      const { data } = await api.post(`/keeper/tickets/${tid}/${action}`, {}, { headers: { "Idempotency-Key": idem("ka", tid, action) } });
      const extra = action === "complete" && data.duration_seconds != null ? ` (${Math.floor(data.duration_seconds / 60)}m ${data.duration_seconds % 60}d)` : "";
      toast.success(`${data.ticket.number} ${ACT_LABEL[action]}${extra}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
      load();
    }
  };
  const routeOp = async (rid, op) => {
    try {
      const { data } = await api.post(`/keeper/routes/${rid}/${op}`, {});
      toast.success(op === "close" ? "Jalur ditutup untuk tiket baru. Antrean lama tetap berjalan." : "Jalur dibuka.");
    } catch (e) {
      toast.error(errMsg(e));
    }
    load();
  };
  if (!b) return <Skeleton className="h-64 rounded-3xl" />;
  const route = b.routes.find((r) => r.id === sel) || b.routes[0];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Check-in", b.stats.checkins, "stat-checkins"], ["Menunggu", b.stats.waiting, "stat-waiting"], ["Selesai", b.stats.completed, "stat-completed"]].map(([l, v, id]) => (
          <div key={l} className="rounded-2xl bg-white py-2 ring-1 ring-[#1A3323]/10"><div className="font-mono text-xl font-extrabold text-[#1A3323]" data-testid={id}>{v}</div><div className="text-[11px] text-muted-foreground">{l}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button className="h-12 min-w-0 rounded-2xl px-2 text-xs font-bold sm:px-3 sm:text-sm" onClick={() => setScan(true)} data-testid="open-scan-button"><ScanLine className="mr-1.5 h-4 w-4 shrink-0 sm:h-5 sm:w-5" /><span className="truncate">Scan QR</span></Button>
        <Button variant="outline" className="h-12 min-w-0 rounded-2xl px-2 text-xs font-bold sm:px-3 sm:text-sm" onClick={() => setManual(true)} data-testid="open-manual-checkin-button"><UserCheck className="mr-1.5 h-4 w-4 shrink-0 sm:h-5 sm:w-5" /><span className="truncate">Check-in Manual</span></Button>
      </div>
      <Button
        variant="outline"
        className="h-11 w-full border-[#3D6F4B] text-[#1A3323]"
        onClick={() => setLeadVerification(true)}
        data-testid="open-lead-verification-button"
      >
        <BadgeCheck className="mr-2 h-4 w-4" />
        Verifikasi Peserta Lead
      </Button>
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1" data-testid="route-tabs">
        {b.routes.map((r) => <RouteTab key={r.id} r={r} offset={offset} active={route?.id === r.id} onClick={() => setSel(r.id)} />)}
      </div>
      {route ? (
        <RoutePanel r={route} offset={offset} busy={busy} act={act} canManage={b.can_manage_routes} onClose={() => routeOp(route.id, "close")} onOpen={() => routeOp(route.id, "open")} />
      ) : (
        <Empty title="Belum ada jalur" desc="Admin perlu menambahkan jalur untuk event ini." />
      )}
      <ScanDialog open={scan} onOpenChange={setScan} eventId={eventId} onAct={act} />
      <LeadVerificationDialog
        open={leadVerification}
        onOpenChange={setLeadVerification}
        eventId={eventId}
      />
      <Dialog open={manual} onOpenChange={setManual}>
        <DialogContent className="max-w-md" data-testid="manual-checkin-dialog">
          <DialogHeader>
            <DialogTitle>Check-in Manual (override GPS)</DialogTitle>
            <DialogDescription>Identifikasi peserta via QR (lebih disarankan, termasuk anak) atau nomor WhatsApp. Waiver tetap wajib. Tercatat di audit log.</DialogDescription>
          </DialogHeader>
          <ManualCheckinForm eventId={eventId} onDone={() => { setManual(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KeeperInner() {
  const { user, signOut } = useAuth();
  const { connected } = useRealtime();
  const nav = useNavigate();
  const [events, setEvents] = useState(null);
  const [eventId, setEventId] = useState(localStorage.getItem("iym_keeper_event"));
  useEffect(() => {
    api.get("/keeper/events").then((r) => {
      setEvents(r.data.items);
      if (!r.data.items.find((e) => e.id === eventId)) setEventId(r.data.items.find((e) => e.status === "PUBLISHED")?.id || r.data.items[0]?.id || null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const refresh = setInterval(() => window.location.reload(), 120000);
    return () => clearInterval(refresh);
  }, []);
  const pick = (id) => { localStorage.setItem("iym_keeper_event", id); setEventId(id); };
  const ev = events?.find((e) => e.id === eventId);
  return (
    <div className="mx-auto min-h-screen max-w-2xl px-4 pb-10">
      <header className="sticky top-0 z-30 -mx-4 mb-4 border-b border-[#1A3323]/10 bg-[#F7F4EA]/95 px-4 py-3 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <Logo className="h-12 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#3D6F4B]">
              Keeper Dashboard
              <span className={cn("h-2 w-2 shrink-0 rounded-full", connected ? "bg-green-500" : "bg-amber-500")} data-testid="keeper-realtime-status" />
            </div>
            <div className="truncate text-sm font-bold text-[#1A3323]">{user?.name}</div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <LanguageSelector />
            {user?.role === "admin" && <Link to="/admin"><Button size="icon" variant="ghost" data-testid="keeper-to-admin-button"><LayoutDashboard className="h-5 w-5" /></Button></Link>}
            <Button size="icon" variant="ghost" onClick={async () => { await signOut(); nav("/"); }} data-testid="keeper-logout-button"><LogOut className="h-5 w-5" /></Button>
          </div>
        </div>
      </header>
      {ev && <div className="mb-3 flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="keeper-event-info"><CalendarDays className="h-4 w-4" /> {fmtDate(ev.date)} · {ev.start_time}–{ev.end_time} · {ev.location}</div>}
      {events && events.length > 1 && (
        <div className="mb-4 rounded-2xl bg-white p-3 ring-1 ring-[#1A3323]/10">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#1A3323]/60">Pilih event operasional</div>
          <Select value={eventId || ""} onValueChange={pick}>
            <SelectTrigger className="h-10 rounded-xl bg-[#F7F4EA] text-left text-sm font-bold [&>span]:truncate" data-testid="keeper-event-select"><SelectValue placeholder="Pilih event" /></SelectTrigger>
            <SelectContent>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {fmtDate(e.date)}{e.status === "CLOSED" ? " (ditutup)" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {events === null ? <Skeleton className="h-64 rounded-3xl" /> : eventId ? <Board key={eventId} eventId={eventId} /> : <Empty title="Belum ada event yang ditugaskan" desc="Minta admin menugaskan kamu ke event." testid="keeper-no-event" />}
    </div>
  );
}

export default function KeeperApp() {
  return <RealtimeProvider><KeeperInner /></RealtimeProvider>;
}
